import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';

const SKIP_SECONDS = 8;
const SESSION_KEY = 'medulla_popup_shown_today';

export default function MedullaPopupAd() {
  const [ad, setAd] = useState(null);
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(SKIP_SECONDS);
  const trackedRef = useRef(false);

  useEffect(() => {
    // Only show once per day per session
    const today = new Date().toISOString().slice(0, 10);
    const shownDate = localStorage.getItem(SESSION_KEY);
    if (shownDate === today) return;

    const load = async () => {
      try {
        const res = await axios.get(`${API}/medulla/serve/popup`);
        if (res.data.ad) {
          setAd(res.data.ad);
          setVisible(true);
          localStorage.setItem(SESSION_KEY, today);
          // Track impression
          if (!trackedRef.current) {
            trackedRef.current = true;
            axios.post(`${API}/medulla/track/${res.data.ad.campaign_id}/impression`).catch(() => {});
          }
        }
      } catch (err) {
        // silent - popup is optional
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, countdown]);

  const handleSkip = () => {
    if (ad) axios.post(`${API}/medulla/track/${ad.campaign_id}/skip`).catch(() => {});
    setVisible(false);
  };

  const handleClick = () => {
    if (ad) {
      axios.post(`${API}/medulla/track/${ad.campaign_id}/click`).catch(() => {});
      window.open(ad.click_url, '_blank', 'noopener');
    }
  };

  if (!visible || !ad) return null;

  return (
    <div
      data-testid="medulla-popup"
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="relative max-w-md w-full bg-zinc-900 border border-green-500/40 rounded-lg overflow-hidden">
        {/* Sponsor tag */}
        <div className="absolute top-2 left-2 text-[10px] font-mono text-green-400 tracking-widest bg-black/70 px-2 py-1 rounded">
          SPONSORED · MEDULLA
        </div>

        {/* Skip button */}
        <button
          data-testid="medulla-skip-btn"
          onClick={handleSkip}
          disabled={countdown > 0}
          className={`absolute top-2 right-2 text-xs px-3 py-1 rounded font-mono ${
            countdown > 0
              ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              : 'bg-white text-black hover:bg-zinc-200'
          }`}
        >
          {countdown > 0 ? `Skip in ${countdown}s` : 'Skip Ad ✕'}
        </button>

        {ad.image_url && (
          <img
            src={ad.image_url}
            alt={ad.title}
            className="w-full h-64 object-cover cursor-pointer"
            onClick={handleClick}
            data-testid="medulla-popup-image"
          />
        )}

        <div className="p-4 space-y-2">
          <h3 className="text-lg font-bold text-white">{ad.title}</h3>
          {ad.description && <p className="text-sm text-zinc-400">{ad.description}</p>}
          <button
            data-testid="medulla-cta-btn"
            onClick={handleClick}
            className="w-full py-2 bg-green-500 hover:bg-green-400 text-black font-bold rounded"
          >
            {ad.cta_text || 'Learn More'}
          </button>
        </div>
      </div>
    </div>
  );
}
